import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Grid,
  HStack,
  Icon,
  IconButton,
  Input,
  InputLeftElement,
  InputRightElement,
  Stack,
  Tag,
  Text,
} from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import {
  MARKUP_DEFAULTS_BY_CONFIGURATION_TYPE,
  MARKUP_KINDS,
  MarkupKind,
  modelUtils,
  prettyCurrency,
} from '@pelicargo/utils';
import { LineItemAudience, MarkupConfigurationType } from '@prisma/client';
import { MouseEvent, useMemo } from 'react';
import { useFieldArray, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { HiChevronDown, HiChevronUp, HiPaperAirplane, HiPlus, HiTrash, HiTruck } from 'react-icons/hi2';

import { SimpleInput } from '../SimpleInput';

export const MARKUP_KIND_DETAILS: Record<MarkupKind, { label: string; description: string; icon: any }> = {
  AIR: { label: 'Air', description: 'Tariff, promo, spot and contract rates', icon: HiPaperAirplane },
  INLAND: { label: 'Inland', description: 'Pickup and transfer (EXW, FCA)', icon: HiTruck },
};

const TIER_COLUMNS = 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.2fr) 32px';

const inputAttrs = { variant: 'outline', px: 3, bg: 'bg.surface' } as const;

type TierRowProps = {
  prefix: string;
  index: number;
  nextMin?: number | string | null;
  onRemove: () => void;
};

const TierRow = ({ prefix, index, nextMin, onRemove }: TierRowProps) => {
  // The server closes each tier at the next tier's start, so "To" is derived, never edited
  const hasNextTier = nextMin !== undefined && nextMin !== null && nextMin !== '';

  return (
    <Grid templateColumns={TIER_COLUMNS} gap="2" alignItems="start">
      <SimpleInput
        name={`${prefix}.costs.${index}.min`}
        type="number"
        aria-label="From weight"
        inputAttrs={{ ...inputAttrs, pr: 10, step: 'any' }}
        rightElement={
          <InputRightElement color="fg.muted" fontSize="sm">
            kg
          </InputRightElement>
        }
      />
      <Box position="relative">
        <Input
          {...inputAttrs}
          pr={10}
          aria-label="To weight"
          value={hasNextTier ? String(nextMin) : ''}
          placeholder="No limit"
          isReadOnly
          tabIndex={-1}
          bg="bg.subtle"
        />
        <Text position="absolute" right="3" top="2.5" fontSize="sm" color="fg.muted">
          kg
        </Text>
      </Box>
      <SimpleInput
        name={`${prefix}.costs.${index}.cost`}
        type="number"
        aria-label="Rate in dollars per kg"
        inputAttrs={{ ...inputAttrs, pl: 6, pr: 12, step: '0.01' }}
        leftElement={
          <InputLeftElement color="fg.muted" w="6">
            $
          </InputLeftElement>
        }
        rightElement={
          <InputRightElement color="fg.muted" fontSize="sm" w="12">
            / kg
          </InputRightElement>
        }
      />
      <IconButton
        aria-label="Remove tier"
        variant="ghost"
        size="sm"
        mt="1"
        icon={<Icon as={HiTrash} fontSize="18" />}
        onClick={onRemove}
      />
    </Grid>
  );
};

type Props = {
  prefix: `markups.${number}`;
  isExpanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
};

/** One Air or Inland markup on a markup configuration, collapsible to a one-line summary */
export const MarkupCard = ({ prefix, isExpanded, onToggle, onRemove }: Props) => {
  const index = Number(prefix.split('.').pop());
  const { setValue } = useFormContext();
  const markup = useWatch({ name: prefix });
  const configurationType: MarkupConfigurationType | undefined = useWatch({ name: 'type' });
  const { fields, append, remove } = useFieldArray({ name: `${prefix}.costs` });
  const { errors } = useFormState({ name: prefix });

  const kind = modelUtils.markup.kind({ applicable_freight_services: markup?.applicable_freight_services });
  const details = kind ? MARKUP_KIND_DETAILS[kind] : undefined;
  const isSeparateLineItem = modelUtils.markup.isSeparateLineItem({ line_item_types: markup?.line_item_types });
  const isShownToCustomer = modelUtils.markup.isShownToCustomer({
    child_itemized_for: markup?.child_itemized_for,
    line_item_types: markup?.line_item_types,
  });
  const hasError = !!(errors?.markups as any)?.[index];
  const title = markup?.label || `New ${details?.label ?? ''} Markup`;

  const summary = useMemo(() => {
    const costs: { cost?: number | string | null }[] = (markup?.costs ?? []).filter(Boolean);
    const rates = costs.map(({ cost }) => Number(cost)).filter((rate) => !isNaN(rate));
    const lowestRate = rates.length ? Math.min(...rates) : 0;
    const rate = `${prettyCurrency(lowestRate, { isCents: false })} / kg`;
    const tiers = costs.length > 1 ? [`${costs.length} tiers`, `from ${rate}`] : [rate];
    const minimum = `Min ${prettyCurrency(Number(markup?.min_cost ?? 0), { isCents: false })}`;
    let visibility = isShownToCustomer ? 'Shown to customer' : 'Internal';
    if (isSeparateLineItem) visibility = 'Shown to customer (manual)';

    return [...tiers, minimum, visibility].join(' · ');
  }, [markup?.costs, markup?.min_cost, isShownToCustomer, isSeparateLineItem]);

  const kindLabel = details?.label ?? 'Air';
  const showToCustomerHelper =
    "Itemize this markup on the customer's quote. When off, it's included in the line item total.";

  const toggleShowToCustomer = (isChecked: boolean) => {
    const itemizedForOthers: LineItemAudience[] = (markup?.child_itemized_for ?? []).filter(
      (audience: LineItemAudience) => audience !== LineItemAudience.CUSTOMER,
    );
    const nextItemizedFor = isChecked ? [...itemizedForOthers, LineItemAudience.CUSTOMER] : itemizedForOthers;
    setValue(`${prefix}.child_itemized_for`, nextItemizedFor, { shouldDirty: true });
  };

  // A separate line item (bundled into nothing) only comes from the classic form. Reset makes it
  // a normal markup of its kind again: bundled into those line items, with this configuration
  // type's audiences, exactly as a newly added one.
  const resetToDefaults = () => {
    const defaults = MARKUP_DEFAULTS_BY_CONFIGURATION_TYPE[configurationType ?? MarkupConfigurationType.TENANT];
    setValue(`${prefix}.line_item_types`, [...MARKUP_KINDS[kind ?? 'AIR'].line_item_types], { shouldDirty: true });
    setValue(`${prefix}.child_itemized_for`, [...defaults.child_itemized_for], { shouldDirty: true });
    setValue(`${prefix}.child_excluded_for`, [...defaults.child_excluded_for], { shouldDirty: true });
  };

  // Both sit inside the clickable card/header, so they mustn't also trigger its toggle
  const handleToggleClick = (event: MouseEvent) => {
    event.stopPropagation();
    onToggle();
  };
  const handleRemoveClick = (event: MouseEvent) => {
    event.stopPropagation();
    onRemove();
  };

  const handleAddTier = () => append({ min: null, cost: null });

  return (
    <Box
      borderWidth="1px"
      borderColor={hasError ? 'error' : isExpanded ? 'border.emphasized' : 'border.default'}
      borderRadius="md"
      bg="bg.surface"
      shadow="sm"
      // Collapsed, the whole card opens it; expanded, only the header closes it, so clicking
      // into the fields below doesn't collapse the card
      cursor={isExpanded ? undefined : 'pointer'}
      _hover={isExpanded ? undefined : { borderColor: 'border.emphasized', bg: 'bg.subtle' }}
      onClick={isExpanded ? undefined : onToggle}
    >
      <HStack
        spacing="3"
        py="3"
        pl="4"
        pr="2"
        bg={isExpanded ? 'bg.subtle' : undefined}
        borderBottomWidth={isExpanded ? '1px' : 0}
        borderTopRadius="md"
        borderBottomRadius={isExpanded ? 0 : 'md'}
        // The chevron stays as the keyboard-reachable control
        cursor={isExpanded ? 'pointer' : undefined}
        _hover={isExpanded ? { bg: 'bg.muted' } : undefined}
        onClick={isExpanded ? onToggle : undefined}
      >
        <Icon as={details?.icon ?? HiPaperAirplane} fontSize="18" color="fg.muted" flex="none" />
        <Stack spacing="0.5" flex="1" minW="0">
          <HStack spacing="2">
            <Text fontSize="md" fontWeight="semibold" noOfLines={1}>
              {title}
            </Text>
            <Show if={!!details}>
              <Tag size="sm" borderRadius="sm">
                {details?.label}
              </Tag>
            </Show>
            <Show if={hasError}>
              <Tag size="sm" borderRadius="sm" colorScheme="red">
                Has errors
              </Tag>
            </Show>
          </HStack>
          <Show if={!isExpanded}>
            <Text fontSize="sm" color="fg.muted" noOfLines={1}>
              {summary}
            </Text>
          </Show>
        </Stack>
        <IconButton
          aria-label={isExpanded ? `Collapse ${title}` : `Edit ${title}`}
          variant="ghost"
          size="sm"
          icon={<Icon as={isExpanded ? HiChevronUp : HiChevronDown} fontSize="18" />}
          onClick={handleToggleClick}
        />
        <IconButton
          aria-label={`Remove ${title}`}
          variant="ghost"
          size="sm"
          icon={<Icon as={HiTrash} fontSize="18" />}
          onClick={handleRemoveClick}
        />
      </HStack>

      <Show if={isExpanded}>
        <Stack spacing="5" p="4">
          <SimpleInput name={`${prefix}.label`} label="Label" placeholder="Base Air Markup" inputAttrs={inputAttrs} />
          <Box w="50%">
            <SimpleInput
              name={`${prefix}.min_cost`}
              label="Minimum charge"
              type="number"
              placeholder="0.00"
              inputAttrs={{ ...inputAttrs, pl: 6, step: '0.01' }}
              leftElement={
                <InputLeftElement color="fg.muted" w="6">
                  $
                </InputLeftElement>
              }
            />
          </Box>
          <Stack spacing="2">
            <Grid templateColumns={TIER_COLUMNS} gap="2">
              <Text fontSize="xs">From</Text>
              <Text fontSize="xs">To</Text>
              <Text fontSize="xs">Rate</Text>
            </Grid>
            {fields.map((field, tierIndex) => (
              <TierRow
                key={field.id}
                prefix={prefix}
                index={tierIndex}
                nextMin={markup?.costs?.[tierIndex + 1]?.min}
                onRemove={() => remove(tierIndex)}
              />
            ))}
            <Button
              variant="ghost"
              alignSelf="flex-start"
              px="2"
              leftIcon={<Icon as={HiPlus} />}
              onClick={handleAddTier}
            >
              Add Tier
            </Button>
          </Stack>
          <Stack spacing="3">
            <Show if={isSeparateLineItem}>
              <Alert status="warning" borderRadius="md" alignItems="flex-start" py="3">
                <AlertIcon mt="0.5" />
                <Stack spacing="0.5" flex="1">
                  <AlertTitle fontSize="sm">Shown to customer by manual configuration</AlertTitle>
                  <AlertDescription fontSize="sm">
                    This markup is its own line item, so everyone sees it. To modify it, reset it to a normal{' '}
                    {kindLabel} markup.
                  </AlertDescription>
                </Stack>
                <Button size="sm" variant="outline" bg="bg.surface" flex="none" onClick={resetToDefaults}>
                  Reset
                </Button>
              </Alert>
            </Show>
            <Stack spacing="1">
              <Checkbox
                isChecked={isShownToCustomer}
                isDisabled={isSeparateLineItem}
                onChange={(event) => toggleShowToCustomer(event.target.checked)}
              >
                Show to customer
              </Checkbox>
              <Show if={!isSeparateLineItem}>
                <Text fontSize="sm" color="fg.muted" pl="6">
                  {showToCustomerHelper}
                </Text>
              </Show>
            </Stack>
          </Stack>
        </Stack>
      </Show>
    </Box>
  );
};
